import { Draft } from '@reduxjs/toolkit';
import { addBoundary } from '../../utils/number';
import { INewFocus, ISelectableState, TBasicRow } from './types';

/**
 * Este método conoce las interfaces necesarias
 * para aplicar las restricciones correspondientes
 * a la hora de hacer foco en una fila, evitando
 * que se haga foco en filas inexistentes.
 */
export function addRowBoundary(newRow: number, state: ISelectableState) {
  const hasFiltersRow = !!state.filters.find(
    (current) =>
      !!state.columns.find((search) => search.name === current.column),
  );
  return addBoundary({
    number: newRow,
    min: hasFiltersRow ? -2 : -1,
    max: Math.max(0, state.rows.length - 1),
  });
}
/**
 * Este método conoce las interfaces necesarias
 * para aplicar las restricciones correspondientes
 * a la hora de hacer foco en una columna, evitando
 * que se haga foco en columnas inexistentes.
 */
export function addColumnBoundary(newColumn: number, state: ISelectableState) {
  return addBoundary({
    number: newColumn,
    min: 0,
    max:
      state.nonAdditionalColumnsCount +
      state.statesColumns.length +
      (state.hasNonAdditionalFilters ? 1 : 0),
  });
}

function isBasicRow(row: unknown): row is TBasicRow {
  return typeof row === 'object' && !!row && 'forbidSelection' in row;
}

function filterNonSellectableRows(
  state: Draft<ISelectableState>,
  newSelection: number[],
) {
  let expandedRowsBefore = 0;
  return newSelection.filter((selectedRowIndex) => {
    const row = state.rows[selectedRowIndex + expandedRowsBefore];
    if (state.expandedRows.includes(selectedRowIndex)) expandedRowsBefore += 1;
    return !(isBasicRow(row) && row.forbidSelection);
  });
}

/**
 * Normalmente, este método será llamado desde un
 * action de Redux, para eso fue diseñado. Su
 * utilidad es la de tomar el estado y alterarlo
 * dependiendo de los parámetros pasados y la lógica
 * de selección de filas y columnas en una grilla,
 * devolviendo un estado coherente que represente
 * las acciones de un usuario en la grilla.
 */
export default function setNewFocused(
  state: Draft<ISelectableState>,
  {
    /**
     * Propiedad proveniente del evento click o keydown.
     */
    ctrlKey,
    /**
     * Propiedad proveniente del evento click o keydown.
     */
    shiftKey,
    newFocusedColumn,
    newFocusedRow,
    /**
     * Con este método se puede seleccionar cuál celda
     * va a recibir el foco y la selección, o solamente
     * el foco si esta propiedad es pasada en false.
     */
    shouldSelectNewFocused,
  }: INewFocus,
) {
  let actualNewFocusedRow =
    Math.round(
      addRowBoundary(newFocusedRow ?? state.focusedRow ?? 0, state) * 2,
    ) / 2;
  const actualNewFocusedColumn = addColumnBoundary(
    newFocusedColumn ?? state.focusedColumn,
    state,
  );

  state.focusedColumn = actualNewFocusedColumn;
  state.focusedRow = actualNewFocusedRow;
  state.isFocused = shouldSelectNewFocused || false;

  if (shouldSelectNewFocused === false || actualNewFocusedRow < 0) return;

  if (Math.round(actualNewFocusedRow) - actualNewFocusedRow !== 0)
    actualNewFocusedRow = Math.floor(actualNewFocusedRow);

  const isDeselectingWithCtrl =
    ctrlKey && state.selectedRows.includes(actualNewFocusedRow);

  if (state.allowSelection !== false)
    if (
      (!ctrlKey && !shiftKey) ||
      (state.isMultiple === false && !isDeselectingWithCtrl)
    )
      state.selectedRows = filterNonSellectableRows(state, [
        actualNewFocusedRow,
      ]);
    else if ((ctrlKey && state.isMultiple) || isDeselectingWithCtrl) {
      state.selectedRows = filterNonSellectableRows(
        state,
        state.selectedRows.includes(actualNewFocusedRow)
          ? state.selectedRows.filter(
              (current) => current !== actualNewFocusedRow,
            )
          : [...state.selectedRows, actualNewFocusedRow],
      );
    } else if (shiftKey && state.isMultiple) {
      const sortedSelectedRows = [...state.selectedRows].sort((a, b) => a - b);
      let minSelectedRow = sortedSelectedRows[0];
      let maxSelectedRow = sortedSelectedRows[sortedSelectedRows.length - 1];

      if (minSelectedRow === undefined) {
        state.selectedRows = filterNonSellectableRows(state, [
          actualNewFocusedRow,
        ]);
        return;
      }

      if (actualNewFocusedRow < minSelectedRow)
        minSelectedRow = actualNewFocusedRow;
      if (actualNewFocusedRow > maxSelectedRow)
        maxSelectedRow = actualNewFocusedRow;

      const newSelectedRows = filterNonSellectableRows(
        state,
        new Array(maxSelectedRow - minSelectedRow + 1)
          .fill('')
          .map((_, i) => i + (minSelectedRow ?? 0)),
      );

      state.selectedRows = filterNonSellectableRows(state, newSelectedRows);
    }
}
